/**
 * Input validation.
 *
 * Two categories, kept strictly separate:
 *
 *   error   — the arithmetic cannot be performed, or produces an undefined /
 *             non-finite result. The tool can determine this.
 *
 *   warning — the arithmetic is valid, but the input is unusual, produces an
 *             extreme output, or has a consequence the user may not have
 *             intended. The tool surfaces it and continues. A warning is not a
 *             statement that an assumption is wrong; only the engineer can
 *             judge that.
 *
 * Deliberately absent: any check that decides whether a temperature, Q10 value,
 * humidity condition or shelf-life claim is *appropriate*.
 */

import type { RawInputs, ValidationIssue, ValidationResult } from '../types';
import { toDays } from './units';

/** Bounds used only to catch physically impossible or non-finite arithmetic. */
export const ABSOLUTE_ZERO_C = -273.15;

/** Bounds used only to trigger explanatory warnings, never to block input. */
export const UNUSUAL_TEMP_LOW_C = -80;
export const UNUSUAL_TEMP_HIGH_C = 150;
export const EXTREME_AAF = 1_000_000;
export const EXTREME_AAT_DAYS = 365 * 100;

function parseNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  // Reject strings like "12abc" that Number() would also reject, and "" / whitespace.
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return value;
}

export function validateInputs(raw: RawInputs): ValidationResult {
  const issues: ValidationIssue[] = [];

  const shelfLife = parseNumber(raw.shelfLifeValue);
  const refTemp = parseNumber(raw.referenceTempC);
  const accTemp = parseNumber(raw.acceleratedTempC);
  const q10 = parseNumber(raw.q10);

  /* ---- errors: arithmetic cannot proceed ---- */

  if (shelfLife === null) {
    issues.push({
      id: 'shelfLife.notNumeric',
      severity: 'error',
      field: 'shelfLifeValue',
      message: 'Enter a number for the desired real-time shelf life.',
      explanation: 'The duration is the numerator of the AAT calculation, so a numeric value is required.',
    });
  } else if (shelfLife <= 0) {
    issues.push({
      id: 'shelfLife.notPositive',
      severity: 'error',
      field: 'shelfLifeValue',
      message: 'Real-time shelf life must be greater than zero.',
      explanation: 'A zero or negative duration has no meaning in this calculation.',
    });
  }

  if (refTemp === null) {
    issues.push({
      id: 'refTemp.notNumeric',
      severity: 'error',
      field: 'referenceTempC',
      message: 'Enter a number for the reference temperature in °C.',
      explanation: 'The reference temperature is part of the exponent term.',
    });
  } else if (refTemp <= ABSOLUTE_ZERO_C) {
    issues.push({
      id: 'refTemp.belowAbsoluteZero',
      severity: 'error',
      field: 'referenceTempC',
      message: `Reference temperature must be above ${ABSOLUTE_ZERO_C} °C.`,
      explanation: 'Temperatures at or below absolute zero are not physically meaningful.',
    });
  }

  if (accTemp === null) {
    issues.push({
      id: 'accTemp.notNumeric',
      severity: 'error',
      field: 'acceleratedTempC',
      message: 'Enter a number for the accelerated aging temperature in °C.',
      explanation: 'The accelerated temperature is part of the exponent term.',
    });
  } else if (accTemp <= ABSOLUTE_ZERO_C) {
    issues.push({
      id: 'accTemp.belowAbsoluteZero',
      severity: 'error',
      field: 'acceleratedTempC',
      message: `Accelerated temperature must be above ${ABSOLUTE_ZERO_C} °C.`,
      explanation: 'Temperatures at or below absolute zero are not physically meaningful.',
    });
  }

  if (q10 === null) {
    issues.push({
      id: 'q10.notNumeric',
      severity: 'error',
      field: 'q10',
      message: 'Enter a number for the Q10 assumption.',
      explanation: 'Q10 is the base of the exponential term.',
    });
  } else if (q10 <= 0) {
    issues.push({
      id: 'q10.notPositive',
      severity: 'error',
      field: 'q10',
      message: 'Q10 must be greater than zero.',
      explanation:
        'A zero or negative base raised to a fractional exponent is undefined in real arithmetic, so no aging factor can be produced.',
    });
  }

  if (raw.startDate !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(raw.startDate)) {
    issues.push({
      id: 'startDate.format',
      severity: 'error',
      field: 'startDate',
      message: 'Start date must be a valid calendar date.',
      explanation: 'The projected completion date is derived by adding whole days to this date.',
    });
  }

  const hasErrors = issues.some((i) => i.severity === 'error');

  /* ---- warnings: valid arithmetic, consequences worth seeing ---- */

  if (!hasErrors && refTemp !== null && accTemp !== null && q10 !== null && shelfLife !== null) {
    if (accTemp === refTemp) {
      issues.push({
        id: 'temps.equal',
        severity: 'warning',
        field: 'acceleratedTempC',
        message: 'Accelerated and reference temperatures are equal, so AAF = 1.',
        explanation:
          'The exponent is zero, so the calculated aging duration equals the real-time duration. The arithmetic is valid; whether that is what you intended is your call.',
      });
    } else if (accTemp < refTemp) {
      issues.push({
        id: 'temps.inverted',
        severity: 'warning',
        field: 'acceleratedTempC',
        message: 'Accelerated temperature is below the reference temperature, so AAF < 1.',
        explanation:
          'The exponent is negative, which yields an aging duration longer than the real-time duration. The arithmetic is valid; interpreting it is an engineering matter.',
      });
    }

    if (q10 === 1) {
      issues.push({
        id: 'q10.unity',
        severity: 'warning',
        field: 'q10',
        message: 'Q10 = 1 produces AAF = 1 at every temperature.',
        explanation: 'With a base of 1, the temperature difference has no effect on the result.',
      });
    }

    for (const [field, value, label] of [
      ['referenceTempC', refTemp, 'Reference'],
      ['acceleratedTempC', accTemp, 'Accelerated'],
    ] as const) {
      if (value < UNUSUAL_TEMP_LOW_C || value > UNUSUAL_TEMP_HIGH_C) {
        issues.push({
          id: `${field}.outsideAnticipatedRange`,
          severity: 'warning',
          field,
          message: `${label} temperature is outside ${UNUSUAL_TEMP_LOW_C} °C to ${UNUSUAL_TEMP_HIGH_C} °C.`,
          explanation:
            'This tool does not restrict the range. The bound exists only to flag values well outside what this calculator was exercised against during development, so you can confirm the entry was intended.',
        });
      }
    }

    const exponent = (accTemp - refTemp) / 10;
    const aaf = Math.pow(q10, exponent);
    const realTimeDays = toDays(shelfLife, raw.shelfLifeUnit);
    const aat = realTimeDays / aaf;

    if (!Number.isFinite(aaf) || aaf === 0) {
      issues.push({
        id: 'aaf.nonFinite',
        severity: 'error',
        field: 'general',
        message: 'The aging factor is not a finite, non-zero number for these inputs.',
        explanation:
          'The combination of Q10 and temperature difference overflows or underflows floating-point arithmetic, so no aging duration can be calculated.',
      });
    } else {
      if (aaf > EXTREME_AAF) {
        issues.push({
          id: 'aaf.extreme',
          severity: 'warning',
          field: 'general',
          message: 'The calculated aging factor is extremely large.',
          explanation:
            'Large factors compress the study into a very short duration. The arithmetic still holds; whether the model extrapolates that far is an engineering question.',
        });
      }
      if (Number.isFinite(aat) && aat > EXTREME_AAT_DAYS) {
        issues.push({
          id: 'aat.extreme',
          severity: 'warning',
          field: 'general',
          message: 'The calculated aging duration exceeds 100 years.',
          explanation: 'Check the shelf-life value, its unit, and the temperature difference.',
        });
      }
      if (Number.isFinite(aat) && aat < 1) {
        issues.push({
          id: 'aat.subDay',
          severity: 'warning',
          field: 'general',
          message: 'The calculated aging duration is less than one day.',
          explanation:
            'Chamber ramp-up and ramp-down time may be a significant fraction of a study this short. This tool does not model chamber dynamics.',
        });
      }
    }
  }

  const ok = !issues.some((i) => i.severity === 'error');

  return {
    ok,
    issues,
    parsed:
      ok && shelfLife !== null && refTemp !== null && accTemp !== null && q10 !== null
        ? {
            realTimeDurationValue: shelfLife,
            realTimeDurationUnit: raw.shelfLifeUnit,
            referenceTempC: refTemp,
            acceleratedTempC: accTemp,
            q10,
            startDate: raw.startDate || undefined,
          }
        : undefined,
  };
}
